import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchNotifications, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications } from '../../store/slices/notificationsSlice';
import PageHeader from '../../components/common/PageHeader';
import { FaBell, FaCheck, FaTrash, FaEnvelope, FaEnvelopeOpen, FaClock, FaSpinner } from 'react-icons/fa';
import './Notifications.css';

const Notifications = () => {
  const dispatch = useDispatch();
  const { notifications, loading, error, pagination } = useSelector((state) => state.notifications);
  const [activeTab, setActiveTab] = useState('all');
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteAllModal, setDeleteAllModal] = useState(false);
  const [success, setSuccess] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await dispatch(markAsRead(notificationId));
      setSuccess('Notification marquée comme lue.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setErrorMsg('Erreur lors de la mise à jour.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await dispatch(markAllAsRead());
      setSuccess('Toutes les notifications ont été marquées comme lues.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setErrorMsg('Erreur lors de la mise à jour.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteNotification(id));
      setDeleteModal(null);
      setSuccess('Notification supprimée.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setErrorMsg('Erreur lors de la suppression.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  const handleDeleteAll = async () => {
    try {
      await dispatch(deleteAllNotifications());
      setDeleteAllModal(false);
      setSuccess('Toutes les notifications ont été supprimées.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setErrorMsg('Erreur lors de la suppression.');
      setTimeout(() => setErrorMsg(''), 3000);
    }
  };

  const getNotificationIcon = (type, read) => {
    if (read) return <FaEnvelopeOpen />;
    return <FaEnvelope />;
  };

  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return !notification.read;
    return notification.type === activeTab;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    // Animation d'entrée pour les notifications
    const notificationItems = document.querySelectorAll('.notification-item');
    notificationItems.forEach((item, index) => {
      item.style.opacity = '0';
      item.style.transform = 'translateY(20px)';
      setTimeout(() => {
        item.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      }, index * 100);
    });

    // Auto-dismiss des alertes
    const alerts = document.querySelectorAll('.alert-modern');
    setTimeout(() => {
      alerts.forEach(alert => {
        alert.style.transition = "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)";
        alert.style.opacity = "0";
        alert.style.transform = "translateY(-20px)";
        setTimeout(() => {
          if (alert.parentNode) {
            alert.remove();
          }
        }, 500);
      });
    }, 5000);

    // Effet de ripple sur les boutons
    const buttons = document.querySelectorAll('.btn-modern');
    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.classList.add('ripple');

        this.appendChild(ripple);

        setTimeout(() => {
          ripple.remove();
        }, 600);
      });
    });

    // Smooth scroll pour les actions
    const actionButtons = document.querySelectorAll('.btn-modern');
    actionButtons.forEach(button => {
      button.addEventListener('mouseenter', function() {
        this.style.transform = 'translateY(-2px) scale(1.02)';
      });

      button.addEventListener('mouseleave', function() {
        this.style.transform = 'translateY(0) scale(1)';
      });
    });

    // Styles CSS pour l'effet ripple
    const style = document.createElement('style');
    style.textContent = `
      .btn-modern {
        position: relative;
        overflow: hidden;
      }

      .ripple {
        position: absolute;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.6);
        transform: scale(0);
        animation: ripple-animation 0.6s linear;
        pointer-events: none;
      }

      @keyframes ripple-animation {
        to {
          transform: scale(4);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }, [notifications]);

  return (
    <main id="main" className="main">
      <div className="modern-container">
        <PageHeader
          title="🔔 Notifications"
          subtitle="Restez informé de vos activités sur LOVA"
        />

        <div className="container">
          {/* Messages de succès/erreur */}
          {success && (
            <div className="alert alert-success alert-modern alert-dismissible fade show" role="alert">
              <FaCheck className="me-2" />
              {success}
              <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
            </div>
          )}

          {errorMsg && (
            <div className="alert alert-danger alert-modern alert-dismissible fade show" role="alert">
              <FaTrash className="me-2" />
              {errorMsg}
              <button type="button" className="btn-close" onClick={() => setErrorMsg('')}></button>
            </div>
          )}

          {/* Statistiques et actions */}
          {notifications.length > 0 && (
            <div className="stats-card">
              <div className="stats-content">
                <div className="stats-info">
                  <h3>{notifications.length}</h3>
                  <p>{notifications.length > 1 ? 'Notifications' : 'Notification'}</p>
                </div>
                <div className="action-buttons">
                  <button type="button" className="btn btn-modern btn-primary-modern" onClick={handleMarkAllAsRead}>
                    <FaCheck />
                    Tout marquer comme lu
                  </button>
                  <button type="button" className="btn btn-modern btn-danger-modern"
                          onClick={() => setDeleteAllModal(true)}>
                    <FaTrash />
                    Tout supprimer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Liste des notifications */}
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <div key={notification.id} className={`notification-item ${!notification.read ? 'notification-unread' : ''}`}>
                <div className="notification-content">
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type, notification.read)}
                  </div>
                  <div className="notification-body">
                    {!notification.read && <span className="badge-new">Nouveau</span>}
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-meta">
                      <span>
                        <FaClock className="me-1" />
                        Reçu le {new Date(notification.created_at).toLocaleDateString('fr-FR')} à {new Date(notification.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {notification.read_at && (
                        <span>
                          <FaCheck className="text-success me-1" />
                          Lu le {new Date(notification.read_at).toLocaleDateString('fr-FR')} à {new Date(notification.read_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="notification-actions">
                    {!notification.read && (
                      <button type="button" className="btn-sm-modern btn-sm-primary" title="Marquer comme lu" onClick={() => handleMarkAsRead(notification.id)}>
                        <FaCheck />
                      </button>
                    )}
                    <button type="button" className="btn-sm-modern btn-sm-danger"
                            onClick={() => setDeleteModal(notification.id)}
                            title="Supprimer">
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <FaBell />
              </div>
              <h4 className="empty-title">Aucune notification</h4>
              <p className="empty-description">Vous n'avez aucune notification pour le moment.</p>
            </div>
          )}

          {/* Pagination moderne */}
          {pagination && pagination.last_page > 1 && (
            <div className="pagination-modern">
              <nav>
                <ul className="pagination">
                  {pagination.current_page > 1 && (
                    <li className="page-item">
                      <button className="page-link" onClick={() => dispatch(fetchNotifications({ page: pagination.current_page - 1 }))}>
                        Précédent
                      </button>
                    </li>
                  )}
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                    <li key={page} className={`page-item ${page === pagination.current_page ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => dispatch(fetchNotifications({ page }))}>
                        {page}
                      </button>
                    </li>
                  ))}
                  {pagination.current_page < pagination.last_page && (
                    <li className="page-item">
                      <button className="page-link" onClick={() => dispatch(fetchNotifications({ page: pagination.current_page + 1 }))}>
                        Suivant
                      </button>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          )}
        </div>

        {/* Modal de confirmation de suppression individuelle */}
        {deleteModal && (
          <div className="modal fade modal-modern show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <FaTrash className="me-2" />
                    Confirmer la suppression
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setDeleteModal(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">Êtes-vous sûr de vouloir supprimer cette notification ?</p>
                  <div className="alert alert-warning">
                    <FaTrash className="me-2" />
                    <strong>Attention :</strong> Cette action est irréversible.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-modern" onClick={() => setDeleteModal(null)} style={{ background: 'var(--secondary-color)', color: 'white' }}>
                    Annuler
                  </button>
                  <button type="button" className="btn btn-modern btn-danger-modern" onClick={() => handleDelete(deleteModal)}>
                    <FaTrash className="me-1" />
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmation de suppression globale */}
        {deleteAllModal && (
          <div className="modal fade modal-modern show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <FaTrash className="me-2" />
                    Confirmer la suppression
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setDeleteAllModal(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">Êtes-vous sûr de vouloir supprimer <strong>toutes</strong> vos notifications ?</p>
                  <div className="alert alert-danger">
                    <FaTrash className="me-2" />
                    <strong>Attention :</strong> Cette action supprimera définitivement toutes vos notifications et ne peut pas être annulée.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-modern" onClick={() => setDeleteAllModal(false)} style={{ background: 'var(--secondary-color)', color: 'white' }}>
                    Annuler
                  </button>
                  <button type="button" className="btn btn-modern btn-danger-modern" onClick={handleDeleteAll}>
                    <FaTrash className="me-1" />
                    Supprimer tout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Backdrop for modals */}
        {(deleteModal || deleteAllModal) && <div className="modal-backdrop fade show" onClick={() => { setDeleteModal(null); setDeleteAllModal(false); }}></div>}
      </div>
    </main>
  );
};

export default Notifications;
